import React from 'react';
import { Link } from 'react-router-dom';

interface Event {
    title: string;
    date: string;
    location: string;
    image: string;
}

const recentEvents: Event[] = [
    {
        title: "International Conference On Life Long Learning",
        date: "Dec 12, 2024",
        location: "Main Auditorium",
        image: "https://images.unsplash.com/photo-1544531586-fde5298cdd40?auto=format&fit=crop&q=80&w=800"
    },
    {
        title: "Cyber Security Meeting",
        date: "Nov 28, 2024",
        location: "Virtual Event",
        image: "https://images.unsplash.com/photo-1550751827-4bd374c3f58b?auto=format&fit=crop&q=80&w=800"
    },
    {
        title: "Career Counselling Session",
        date: "Nov 15, 2024",
        location: "Community Hall",
        image: "https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&q=80&w=800"
    }
];

const upcomingEvents: Event[] = [
    {
        title: "Women Empowerment Conference",
        date: "Jan 10, 2025",
        location: "Convention Center",
        image: "https://images.unsplash.com/photo-1600880292203-757bb62b4baf?auto=format&fit=crop&q=80&w=800"
    },
    {
        title: "Health & Wellness Workshop",
        date: "Jan 22, 2025",
        location: "Community Center",
        image: "https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?auto=format&fit=crop&q=80&w=800"
    },
    {
        title: "Tech for Good Hackathon",
        date: "Feb 5, 2025",
        location: "Innovation Hub",
        image: "https://images.unsplash.com/photo-1555066931-4365d14bab8c?auto=format&fit=crop&q=80&w=800"
    }
];

const EventCard: React.FC<{ event: Event; index: number }> = ({ event, index }) => (
    <div
        className="group bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1 animate-fade-in-up"
        style={{ animationDelay: `${index * 0.1}s` }}
    >
        {/* Image Container */}
        <div className="relative overflow-hidden aspect-[4/3] bg-gray-100">
            <img
                src={event.image}
                alt={event.title}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            />
            <div className="absolute top-4 right-4 bg-white/90 text-primary text-xs font-bold px-3 py-1 rounded-full shadow-sm">
                {event.date}
            </div>
        </div>

        {/* Content - Always Visible */}
        <div className="p-5">
            <h3 className="text-lg font-bold text-gray-900 mb-1 line-clamp-2" title={event.title}>
                {event.title}
            </h3>
            <div className="flex items-center text-gray-600 text-sm">
                <svg className="w-4 h-4 mr-1.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span className="truncate">{event.location}</span>
            </div>
        </div>
    </div>
);

interface EventsProps {
    showOnly?: 'recent' | 'upcoming' | 'all';
}

const Events: React.FC<EventsProps> = ({ showOnly = 'all' }) => {
    return (
        <section id="events" className="pt-0 pb-20 bg-white">
            <div className="container mx-auto px-4">
                <p className="text-center text-gray-700 mb-12 text-lg">
                    Do you have an idea for an event?{' '}
                    <Link to="/contact" className="text-primary font-semibold hover:underline">
                        Contact us
                    </Link>
                </p>
                {/* Recent Events Section */}
                {(showOnly === 'all' || showOnly === 'recent') && (
                    <div id="recent-events" className={showOnly === 'all' ? 'mb-20' : ''}>
                        <div className="flex flex-col md:flex-row justify-between items-end mb-12">
                            <div>
                                <h4 className="text-secondary font-bold uppercase tracking-widest mb-2 text-sm">Gallery</h4>
                                <h2 className="text-3xl md:text-5xl font-bold text-gray-900">
                                    <strong>Recent Events</strong>
                                </h2>
                            </div>
                            {showOnly !== 'all' && (
                                <div className="mt-4 md:mt-0">
                                    <Link to="/events" className="text-primary font-bold hover:text-primary-dark transition-colors flex items-center">
                                        View All Events
                                        <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                                        </svg>
                                    </Link>
                                </div>
                            )}
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                            {recentEvents.map((event, index) => (
                                <EventCard key={`recent-${index}`} event={event} index={index} />
                            ))}
                        </div>
                    </div>
                )}

                {/* Upcoming Events Section */}
                {(showOnly === 'all' || showOnly === 'upcoming') && (
                    <div id="upcoming-events">
                        <div className="flex flex-col md:flex-row justify-between items-end mb-12">
                            <div>
                                <h4 className="text-secondary font-bold uppercase tracking-widest mb-2 text-sm">Coming Soon</h4>
                                <h2 className="text-3xl md:text-5xl font-bold text-gray-900">
                                    <strong>Upcoming Events</strong>
                                </h2>
                            </div>
                            {showOnly !== 'all' && (
                                <div className="mt-4 md:mt-0">
                                    <Link to="/events" className="text-primary font-bold hover:text-primary-dark transition-colors flex items-center">
                                        View All Events
                                        <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                                        </svg>
                                    </Link>
                                </div>
                            )}
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                            {upcomingEvents.map((event, index) => (
                                <EventCard key={`upcoming-${index}`} event={event} index={index} />
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </section>
    );
};

// This is a named export, but we'll also provide a default export for backward compatibility
export { Events as default };
